import * as React from 'react'

import { Button, Spinner } from '@factorialco/deprecated-design-system'
import { RemoveLegalEntityPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
// Hooks
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import legalEntities from 'modules/core/store/collections/legalEntities'

import { legalEntityEmployees } from 'modules/employees/store/collections/employeeGroupEmployees'
import { legalEntityEmployeeGroups } from 'modules/employees/store/collections/employeeGroups'
import { type Employees } from 'modules/employees/store/collections/employees'
import LegalEntitySelectForm from 'modules/legal_entities/lib/forms/LegalEntitySelectForm'

import RemoveLegalEntityWithDependencies from './RemoveLegalEntityWithDependencies'
import FinanceAlert from './_FinanceAlert'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  onClose: ModalProps['onClose']
  legalEntityId: number
  employees: Employees
}

const RemoveLegalEntity = ({ onClose, legalEntityId, employees }: Props) => {
  const { data, loading } = useQuery(RemoveLegalEntityPageDocument, {
    variables: {
      legalEntityId,
    },
  })

  const supplements = data?.payroll.supplements ?? []
  const onboardings = data?.banking.onboardingsConnection.nodes ?? []
  const financialDocuments = data?.finance.financialDocumentsConnection.nodes ?? []

  const [openModal, setOpenModal] = React.useState(false)
  const form = React.useRef(new LegalEntitySelectForm({})).current

  const legalEntity = React.useMemo(() => {
    return legalEntities.get(legalEntityId)
  }, [legalEntityId])

  const options = React.useMemo(() => {
    if (!legalEntity) return []

    return legalEntities.models
      .filter((l) => legalEntity.attributes.id !== l.attributes.id)
      .filter((l) => legalEntityEmployeeGroups(l).length > 0)
      .map((legalEntityInCollection) => ({
        label: legalEntityInCollection.get('legal_name'),
        value: legalEntityInCollection.get('id'),
      }))
  }, [legalEntity])

  const newLegalEntityId = Number(form.get('legal_entity_id').value)

  const employeeGroupOptions = React.useMemo(() => {
    const newLegalEntity = legalEntities.get(newLegalEntityId)

    if (!newLegalEntity) return []

    return legalEntityEmployeeGroups(newLegalEntity).map((employeeGroup) => ({
      label: employeeGroup.attributes.name,
      value: employeeGroup.attributes.id,
    }))
  }, [newLegalEntityId])

  const newEmployeeGroupId = Number(form.get('employee_group_id').value)
  const blockedByFinance = financialDocuments.length > 0 || onboardings.length > 0

  const disabled = React.useMemo(() => {
    if (!legalEntity) return true

    if (legalEntities.models.length < 2) return true

    if (blockedByFinance) return true

    const filled = !!newLegalEntityId && !!newEmployeeGroupId

    return !!legalEntityEmployees(legalEntity, employees).length && !filled
  }, [legalEntity, newLegalEntityId, newEmployeeGroupId, blockedByFinance])

  const hasSupplements = supplements.length > 0

  const { running: destroying, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const employeesList = legalEntity ? legalEntityEmployees(legalEntity, employees) : []

      if (hasSupplements) {
        await ApiClient().post('/payroll/supplements/bulk_destroy_legal_entity_supplements', {
          legal_entity_id: legalEntity?.id,
        })
      }
      if (employeesList && employeesList.length) {
        const promises = employeesList.map((employee) =>
          employee.save({
            legal_entity_id: form.get('legal_entity_id').value,
            employee_group_id: form.get('employee_group_id').value,
          })
        )
        await Promise.all(promises)
      }

      await legalEntity?.destroy()
      setOpenModal(false)
      onClose && onClose()
      closePortal()
    }
  )

  if (!legalEntity) return null

  const hasEmployees = !!legalEntityEmployees(legalEntity, employees).length

  const description = hasEmployees
    ? i18n.t('settings.legal_entities.legal_entity.remove.with_employees')
    : i18n.t('settings.legal_entities.legal_entity.remove.description')

  return (
    <>
      <Modal
        illustration="alert"
        title={i18n.t('settings.legal_entities.legal_entity.remove.title', {
          legal_entity: legalEntity.name,
        })}
        description={blockedByFinance ? null : description}
        onClose={onClose}
        onSubmit={onSubmit}
        footer={
          loading ? (
            <Spinner />
          ) : hasSupplements ? (
            <>
              <Button type="secondary" onClick={onClose}>
                {i18n.t('settings.legal_entities.legal_entity.remove.cancel')}
              </Button>
              <Button
                disabled={disabled || destroying}
                type="negative"
                onClick={() => {
                  setOpenModal(true)
                }}
              >
                {i18n.t('settings.legal_entities.legal_entity.remove.next')}
              </Button>
            </>
          ) : (
            <Button disabled={disabled || destroying} type="negative" submit>
              {i18n.t('settings.legal_entities.legal_entity.remove.submit')}
            </Button>
          )
        }
      >
        {blockedByFinance && (
          <FinanceAlert
            financialDocumentsCount={financialDocuments.length}
            bankAccountsCount={onboardings.length}
          />
        )}

        {hasEmployees && !blockedByFinance && (
          <>
            <FormRow>
              <Select
                none={i18n.t('common.none')}
                field={form.get('legal_entity_id')}
                label={i18n.t('common.legal_entity')}
                options={options}
              />
            </FormRow>
            {newLegalEntityId > 0 && (
              <FormRow>
                <Select
                  none={i18n.t('common.none')}
                  field={form.get('employee_group_id')}
                  label={i18n.t('common.employee_group')}
                  options={employeeGroupOptions}
                />
              </FormRow>
            )}
          </>
        )}
      </Modal>
      {openModal && (
        <RemoveLegalEntityWithDependencies
          supplements={supplements}
          legalEntity={legalEntity}
          onClose={() => setOpenModal(false)}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}

export default observer(RemoveLegalEntity)
